import { Trans } from '@lingui/macro'
import { ReactComponent as ArrowDown } from 'assets/svg/arrow-down.svg'
import PrefetchBalancesWrapper from 'components/AccountDrawer/PrefetchBalancesWrapper'
import Loader from 'components/Icons/LoadingSpinner'
import { IconWrapper } from 'components/Identicon/StatusIcon'
import { useConnectModal } from 'connection/wallet-connect'
import { useOnClickOutside } from 'hooks/useOnClickOutside'
import { Row } from 'nft/components/Flex'
import { useIsNftClaimAvailable } from 'nft/hooks/useIsNftClaimAvailable'
import { useMemo, useRef, useState } from 'react'
import { Box } from 'rebass/styled-components'
import { isTransactionRecent, useAllTransactions } from 'state/transactions/hooks'
import { TransactionDetails } from 'state/transactions/types'
import styled from 'styled-components/macro'
import { flexRowNoWrap } from 'theme/styles'
import { shortenAddress } from 'utils'
import { useAccount, useEnsName } from 'wagmi'

import { AccountPopoverDetails } from '../AccountPopoverDetails'
import { ButtonPrimary, ButtonSecondary } from '../Button'
import { WalletIcon } from '../Icons/Wallet'
import { RowBetween } from '../Row'

// https://stackoverflow.com/a/31617326
const FULL_BORDER_RADIUS = 9999

const Web3StatusGeneric = styled(ButtonSecondary)`
  ${flexRowNoWrap};
  width: 100%;
  align-items: center;
  padding: 10px;
  border-radius: 8px;
  cursor: pointer;
  user-select: none;
  height: 48px;
  margin-right: 2px;
  margin-left: 2px;
  :focus {
    outline: none;
  }
`
// const Web3StatusError = styled(Web3StatusGeneric)`
//   background-color: ${({ theme }) => theme.accentFailure};
//   border: 1px solid ${({ theme }) => theme.accentFailure};
//   color: ${({ theme }) => theme.white};
//   font-weight: 500;
//   :hover,
//   :focus {
//     background-color: ${({ theme }) => darken(0.1, theme.accentFailure)};
//   }
// `

const Web3StatusConnectWrapper = styled.div<{ faded?: boolean }>`
  ${flexRowNoWrap};
  align-items: center;
  background-color: ${({ theme }) => theme.accentActionSoft};
  border-radius: ${FULL_BORDER_RADIUS}px;
  border: none;
  padding: 0;
  height: 44px;

  color: ${({ theme }) => theme.textPrimary};
  :hover {
    color: ${({ theme }) => theme.accentActionSoft};
    stroke: ${({ theme }) => theme.accentActionSoft};
  }

  transition: ${({
    theme: {
      transition: { duration, timing },
    },
  }) => `${duration.fast} color ${timing.in}`};
`

const Web3StatusConnected = styled(Web3StatusGeneric)<{
  pending?: boolean
  isClaimAvailable?: boolean
}>`
  background-color: ${({ theme }) => theme.buttonSecondary};
  border: 1px solid ${({ theme }) => theme.borderSecondary};
  color: ${({ pending, theme }) => (pending ? theme.white : theme.textPrimary)};
  font-weight: 400;
  :hover,
  :focus {
    background-color: ${({ theme }) => theme.buttonSecondaryHover};
    border: 1px solid ${({ theme }) => theme.borderSecondary};
    box-shadow: none;

    :focus {
      border: 1px solid ${({ theme }) => theme.borderSecondary};
      box-shadow: none;
    }
  }

  & > svg {
    color: ${({ theme }) => theme.textSecondary};
  }

  @media only screen and (max-width: ${({ theme }) => `${theme.breakpoint.lg}px`}) {
    padding: 8px 12px;
    width: auto;
    height: 40px;
    border-radius: 8px;

    ${IconWrapper} {
      margin-right: 0;
    }
  }
`

const StatusIconWrapper = styled.div`
  height: 24px;

  @media only screen and (max-width: ${({ theme }) => `${theme.breakpoint.navSearchInputVisible}px`}) {
    display: none;
  }
`

const AddressAndChevronContainer = styled.div`
  display: flex;
`

const Text = styled.p`
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin: 0 0.5rem 0 0.25rem;
  font-size: 14px;
  line-height: 22px;
  width: fit-content;
  font-weight: 400;
  color: ${({ theme }) => theme.textPrimary};
`

// const NetworkIcon = styled(AlertTriangle)`
//   margin-left: 0.25rem;
//   margin-right: 0.5rem;
//   width: 16px;
//   height: 16px;
// `

// we want the latest one to come first, so return negative if a is after b
function newTransactionsFirst(a: TransactionDetails, b: TransactionDetails) {
  return b.addedTime - a.addedTime
}

const StyledConnectButton = styled(ButtonPrimary)`
  font-weight: 400;
  font-size: 14px;
  padding: 10px 12px;
`

function Web3StatusInner({ onOpenPopover }: { onOpenPopover: () => void }) {
  const { address: account } = useAccount()
  const { data: ENSName } = useEnsName()
  const { open } = useConnectModal()

  const isClaimAvailable = useIsNftClaimAvailable((state) => state.isClaimAvailable)

  // const error = useAppSelector((state) => state.connection.errorByConnectionType[status as unknown as ConnectionType])

  const allTransactions = useAllTransactions()

  const sortedRecentTransactions = useMemo(() => {
    const txs = Object.values(allTransactions)
    return txs.filter(isTransactionRecent).sort(newTransactionsFirst)
  }, [allTransactions])

  const pending = sortedRecentTransactions.filter((tx) => !tx.receipt).map((tx) => tx.hash)

  const hasPendingTransactions = !!pending.length

  if (account) {
    return (
      <Web3StatusConnected
        data-testid="web3-status-connected"
        onClick={onOpenPopover}
        pending={hasPendingTransactions}
        isClaimAvailable={isClaimAvailable}
      >
        <Row>
          {!hasPendingTransactions && (
            <StatusIconWrapper>
              <WalletIcon color="#957C89" />
            </StatusIconWrapper>
          )}
          {hasPendingTransactions ? (
            <RowBetween>
              <Text>
                <Trans>{pending?.length} Pending</Trans>
              </Text>{' '}
              <Loader stroke="white" />
            </RowBetween>
          ) : (
            <>
              <AddressAndChevronContainer>
                <Text>{ENSName || shortenAddress(account)}</Text>
              </AddressAndChevronContainer>
              <ArrowDown />
            </>
          )}
        </Row>
      </Web3StatusConnected>
    )
  } else {
    return (
      <Web3StatusConnectWrapper
        tabIndex={0}
        faded={!account}
        onKeyPress={(e) => e.key === 'Enter' && open()}
        onClick={open}
      >
        <StyledConnectButton tabIndex={-1} data-testid="navbar-connect-wallet">
          <Row gap="8">
            <WalletIcon />
            <Trans>Connect Wallet</Trans>
          </Row>
        </StyledConnectButton>
      </Web3StatusConnectWrapper>
    )
  }
}

export default function Web3Status() {
  const accountPopoverRef = useRef<HTMLDivElement | null>(null)
  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false)

  const handleWalletDropdownClick = () => {
    setIsAccountDropdownOpen((prev) => !prev)
  }

  useOnClickOutside(accountPopoverRef, isAccountDropdownOpen ? () => setIsAccountDropdownOpen(false) : undefined)

  return (
    <PrefetchBalancesWrapper>
      <Box ref={accountPopoverRef} sx={{ position: 'relative' }}>
        <Web3StatusInner onOpenPopover={handleWalletDropdownClick} />
        {isAccountDropdownOpen && <AccountPopoverDetails onClose={() => setIsAccountDropdownOpen(false)} />}
      </Box>
    </PrefetchBalancesWrapper>
  )
}
